import { $actionEvent } from '../actions/actionEvent';
import { $cell, editCellPos } from './RichTextEditor';
import {
  COLS_BAR_HEIGHT,
  ROWS_BAR_WIDTH,
  SCROLL_MARGIN_X,
  SCROLL_MARGIN_Y,
} from './consts';
import {
  $tempSelection,
  $tempUnion,
  addTempSelection,
  clearSelections,
  setTempEnd,
  useSelectedAndAdjacent,
} from './selection';
import { cellStyleToCss } from '@/model/cellStyle';
import { RectRange } from '@/model/range';
import { richTextToHtml } from '@/model/richText';
import type { Sheet } from '@/model/sheet';
import type { Vector2 } from '@/utils/vec';
import { useStore } from '@nanostores/react';
import clsx from 'clsx';
import { atom } from 'nanostores';
import { useCallback, useEffect, useRef, useState } from 'react';

export const $dragSelecting = atom(false);

export function CellView({
  pos,
  size,
  cellPos,
  sheet,
}: {
  pos: Vector2;
  size: Vector2;
  cellPos: Vector2;
  sheet: Sheet;
}) {
  const divRef = useRef<HTMLDivElement>(null);
  const [rtDiv, setRtDiv] = useState<HTMLElement | null>(null);
  const [selected, aboveS, belowS, leftS, rightS] =
    useSelectedAndAdjacent(cellPos);
  const cell = useStore(sheet.cellStore(cellPos));
  const { style, value } = cell;
  const { background } = style;
  const { borderLeft, borderTop, verticalAlign, justify } = style;
  const rtPos = useStore($cell)?.pos;

  useEffect(() => {
    if (!rtDiv) return;
    // eslint-disable-next-line react-hooks/immutability
    rtDiv.innerHTML = '';
    rtDiv.append(richTextToHtml(value, style));
  }, [rtDiv, style, value]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!e.shiftKey) {
        clearSelections();
      }
      $tempSelection.set(new RectRange(cellPos, cellPos));
      $tempUnion.set(!selected || !e.shiftKey);
      $dragSelecting.set(true);

      $actionEvent.emit('highlight', { pos: cellPos, dontClear: true });

      function onMouseUp() {
        $dragSelecting.set(false);
        addTempSelection();
        window.removeEventListener('mouseup', onMouseUp);
      }
      window.addEventListener('mouseup', onMouseUp);
    },
    [cellPos, selected],
  );

  const onMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!$dragSelecting.get()) return;
      setTempEnd(cellPos);
      divRef.current?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth',
      });
    },
    [cellPos],
  );

  if (size.x === 0 || size.y === 0) return null;

  return (
    <div
      ref={divRef}
      className={clsx(
        'absolute flex whitespace-break-spaces select-none',
        selected &&
          'after:absolute after:inset-0 after:border-2 after:border-red-500 after:bg-red-500/20',
        aboveS && 'after:border-t-0',
        belowS && 'after:border-b-0',
        leftS && 'after:border-l-0',
        rightS && 'after:border-r-0',
      )}
      style={{
        left: pos.x,
        top: pos.y,
        width: size.x,
        height: size.y,
        scrollMarginTop: COLS_BAR_HEIGHT + SCROLL_MARGIN_Y,
        scrollMarginLeft: ROWS_BAR_WIDTH + SCROLL_MARGIN_X,
        scrollMarginRight: SCROLL_MARGIN_X,
        scrollMarginBottom: SCROLL_MARGIN_Y,
        background: background?.toHex(),
      }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onDoubleClick={() => {
        editCellPos(cellPos, sheet);
        clearSelections();
      }}
    >
      <div
        className="absolute top-0 right-0 left-0 -translate-y-1/2"
        style={{
          borderTopWidth: borderTop.width,
          borderTopColor: borderTop.color.toHex(),
          borderTopStyle: borderTop.style,
          // TODO: style double isn't right. Should have a 2 layer border.
        }}
      />
      <div
        className="absolute top-0 bottom-0 left-0 -translate-x-1/2"
        style={{
          borderLeftWidth: borderLeft.width,
          borderLeftColor: borderLeft.color.toHex(),
          borderLeftStyle: borderLeft.style,
        }}
      />
      {(!rtPos || !cellPos.equals(rtPos)) && (
        <div
          className={clsx(
            'absolute inset-0 flex overflow-hidden',
            justify === 'center' && 'justify-center',
            justify === 'right' && 'justify-end',
            verticalAlign === 'middle' && 'items-center',
            verticalAlign === 'bottom' && 'items-end',
          )}
        >
          <div
            ref={setRtDiv}
            className="min-h-fit w-full min-w-fit text-nowrap whitespace-pre [font-synthesis:none]"
            style={{
              ...cellStyleToCss(style),
              fontSize: 0, // MUST have this otherwise the browser sets a "minimum" line height
              backgroundColor: 'transparent',
            }}
          />
        </div>
      )}
    </div>
  );
}
